import type { QueryClient, UseQueryOptions } from '@tanstack/react-query';
import { GraphQLClient, gql } from 'graphql-request';

import type {
  GraphQLSupporter,
  GraphQLSupporterContact,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { isObject } from '@sola/shared-utils/misc.ts';

import { MOCK_SUPPORTER, QUERY_KEYS } from './constants.ts';

const graphqlEndpoint =
  process.env.GRAPHQL_PROXY_DESTINATION ||
  `${process.env.NEXT_PUBLIC_BASE_URL_PROXY_API}/graphql`;

/**
 * GraphQL Client
 * If needbe, we can configure error handling in the middleware.
 */
const client = new GraphQLClient(graphqlEndpoint, {
  credentials: 'include',
});

type GraphQLErrorResponse = {
  message: string;
};

export const isGraphQLResponseError = (
  response: unknown,
): response is GraphQLErrorResponse => {
  return (
    isObject(response) && 'message' in (response as Record<string, unknown>)
  );
};

type SelectedSupporterData<TKey> = TKey extends keyof GraphQLSupporter
  ? Pick<GraphQLSupporter, TKey>
  : Pick<GraphQLSupporter, keyof GraphQLSupporter>;

type SupporterField<TKey extends keyof GraphQLSupporter> = NonNullable<
  GraphQLSupporter[TKey]
>;

/**
 * Response from a graphql query.
 * It allows us to specify the data being returned, which is generally recommended.
 */
type SupporterResponse<TKey extends keyof GraphQLSupporter> =
  | {
      supporter: SelectedSupporterData<TKey>;
      // null is to handle errors, double check this.
    }
  | GraphQLErrorResponse;

type SupporterBundleResponse<TData> =
  | {
      supporter: TData;
    }
  | GraphQLErrorResponse;

export type SupporterQueryContext = {
  queryClient: QueryClient;
};

export type SupporterQuery<TData> = Omit<UseQueryOptions<TData>, 'queryFn'> & {
  queryFn: (
    globalId: string,
    queryContext?: SupporterQueryContext,
  ) => Promise<TData>;
  waitForPrimer?: boolean;
};

export type InferSupporterQueryData<TQuery extends SupporterQuery<unknown>> =
  Awaited<ReturnType<TQuery['queryFn']>>;

export type SupporterDetailsData = Pick<
  GraphQLSupporter,
  | 'displayName'
  | 'firstName'
  | 'lastName'
  | 'preferredName'
  | 'salutation'
  | 'suffix'
  | 'organizationName'
  | 'email'
  | 'mobilePhone'
  | 'gender'
  | 'primaryLanguage'
  | 'correspondenceDeliveryPreference'
  | 'isGroup'
  | 'undeliverableEmail'
  | 'undeliverablePhone'
  | 'type'
  | 'typeLevel2'
  | 'typeLevel3'
  | 'status'
  | 'recordType'
  | 'sourceCode'
  | 'globalId'
>;

export type PrimedSupporterData = Pick<
  GraphQLSupporter,
  | 'dataStatus'
  | 'addressDetails'
  | 'commitments'
  | 'givingTotals'
  | 'paymentMethods'
  | 'pledges'
> & {
  details: SupporterDetailsData | null;
};

export type SupporterQueryPart = keyof PrimedSupporterData;

type SupporterDocumentPart = {
  fragment: string;
  selection: string;
};

type SupporterBundlePartData = Partial<PrimedSupporterData>;

type SupporterBundleParts<TParts extends readonly SupporterQueryPart[]> = Pick<
  PrimedSupporterData,
  TParts[number]
>;

const defaultSupporterDataStatus: SupporterField<'dataStatus'> = {
  isPartial: false,
  issues: [],
};

const supporterDataStatusFragment = gql`
  fragment SupporterDataIssueFields on SupporterDataIssue {
    key
    message
    retryable
    stale
    lastAttemptAt
  }

  fragment SupporterDataStatusFields on SupporterDataStatus {
    isPartial
    issues {
      ...SupporterDataIssueFields
    }
  }
`;

const supporterAddressDetailsFragment = gql`
  fragment SupporterAddressDetailsFields on SupporterAddress {
    street
    street2
    city
    state
    postalCode
    country
    isUndeliverable
  }
`;

const supporterCommitmentsFragment = gql`
  fragment SupporterCommitmentsFields on Commitment {
    amountDue
    participant {
      birthdate
      country
      gender
      globalId
      localId
      name
      photoUrl
    }
    commitmentType
    canGiveGifts
    canMakePayments
    canWrite
    dates {
      start
      end
    }
    frequency
    fund {
      code
      description
      icon
    }
    globalId
    internalId
    interventionId
    isCorrespondent
    isRecurring
    lastCorrespondenceDate
    paymentDateNext
    paymentDatePending
    hasMissedPayment
    missedPaymentAmount
    hasDueThisMonth
    dueThisMonthAmount
    paymentFrequency
    paymentMethodId
    paymentPullDay
    rate
    receiptorId
    sourceCode
    status
    transactionId
  }
`;

const supporterGivingTotalsFragment = gql`
  fragment SupporterGivingTotalsFields on GivingTotals {
    amountDue
    annual
    monthly
    quarterly
    semiAnnual
    totalMissedPaymentAmount
    totalDueThisMonthAmount
  }
`;

const supporterPaymentMethodsFragment = gql`
  fragment SupporterPaymentMethodsFields on SupporterPaymentMethod {
    id
    status
    accountNickname
    billingAddress {
      attention
      street
      city
      stateCode
      postalCode
      country
    }
    paymentType
    creditCard {
      cardholderName
      cardType
      digitalType
      expirationDate
      expiryStatus
      lastFour
      responseCode
      token
    }
    ach {
      accountName
      lastFourOfAccountNumber
      routingNumber
      token
    }
  }
`;

const supporterPledgesFragment = gql`
  fragment SupporterPledgesFields on Pledge {
    totalPledgeAmount
    totalGivenToDate
    status
    startDate
    pledgeName
    pledgeId
    outstandingAmount
    nextInstallmentDate
    nextInstallmentAmount
    lastInstallmentDate
    lastInstallmentAmount
    endDate
  }
`;

const supporterContactsFragment = gql`
  fragment SupporterContactsFields on SupporterContact {
    id
    firstName
    lastName
    preferredName
    email
    phone
  }
`;

const supporterDetailsFragment = gql`
  fragment SupporterDetailsFields on Supporter {
    displayName
    firstName
    lastName
    preferredName
    salutation
    suffix
    organizationName
    email
    mobilePhone
    gender
    globalId
    primaryLanguage
    correspondenceDeliveryPreference
    isGroup
    undeliverableEmail
    undeliverablePhone
    type
    typeLevel2
    typeLevel3
    status
    recordType
    sourceCode
  }
`;

const supporterDocumentParts = {
  dataStatus: {
    fragment: supporterDataStatusFragment,
    selection: `
      dataStatus {
        ...SupporterDataStatusFields
      }
    `,
  },
  addressDetails: {
    fragment: supporterAddressDetailsFragment,
    selection: `
      addressDetails {
        ...SupporterAddressDetailsFields
      }
    `,
  },
  commitments: {
    fragment: supporterCommitmentsFragment,
    selection: `
      commitments {
        ...SupporterCommitmentsFields
      }
    `,
  },
  givingTotals: {
    fragment: supporterGivingTotalsFragment,
    selection: `
      givingTotals {
        ...SupporterGivingTotalsFields
      }
    `,
  },
  paymentMethods: {
    fragment: supporterPaymentMethodsFragment,
    selection: `
      paymentMethods {
        ...SupporterPaymentMethodsFields
      }
    `,
  },
  pledges: {
    fragment: supporterPledgesFragment,
    selection: `
      pledges {
        ...SupporterPledgesFields
      }
    `,
  },
  details: {
    fragment: supporterDetailsFragment,
    selection: `
      ...SupporterDetailsFields
    `,
  },
} satisfies Record<SupporterQueryPart, SupporterDocumentPart>;

const supporterQueryPartKeys = {
  dataStatus: [QUERY_KEYS.supporterDataStatus],
  addressDetails: [QUERY_KEYS.supporterAddress],
  commitments: [QUERY_KEYS.supporterCommitments],
  givingTotals: [QUERY_KEYS.supporterGivingTotals],
  paymentMethods: [QUERY_KEYS.supporterPaymentMethods],
  pledges: [QUERY_KEYS.supporterPledges],
  details: [QUERY_KEYS.supporterDetails],
} as const satisfies Record<SupporterQueryPart, readonly [string]>;

const getSupporterQueryPartKey = (
  part: SupporterQueryPart,
  globalId?: string,
) => [...supporterQueryPartKeys[part], globalId] as const;

const buildSupporterQueryDocument = (
  operationName: string,
  parts: readonly SupporterQueryPart[],
) => {
  const fragments = [
    ...new Set(parts.map((part) => supporterDocumentParts[part].fragment)),
  ].join('\n');
  const selections = parts
    .map((part) => supporterDocumentParts[part].selection)
    .join('\n');

  return gql`
    ${fragments}
    query ${operationName}($globalId: String!) {
      supporter(globalId: $globalId) {
        ${selections}
      }
    }
  `;
};

const supporterBundleDocumentCache = new Map<string, string>();

const getSupporterBundleDocument = (
  operationName: string,
  parts: readonly SupporterQueryPart[],
) => {
  const cacheKey = `${operationName}:${parts.join('|')}`;
  const cachedDocument = supporterBundleDocumentCache.get(cacheKey);

  if (cachedDocument) {
    return cachedDocument;
  }

  const document = buildSupporterQueryDocument(operationName, parts);
  supporterBundleDocumentCache.set(cacheKey, document);

  return document;
};

const primedSupporterParts = [
  'dataStatus',
  'addressDetails',
  'commitments',
  'givingTotals',
  'paymentMethods',
  'pledges',
  'details',
] as const satisfies readonly SupporterQueryPart[];

const paymentMethodsDocument = buildSupporterQueryDocument(
  'GetSupporterPaymentMethods',
  ['paymentMethods'],
);

const supporterAddressDocument = buildSupporterQueryDocument(
  'GetSupporterAddress',
  ['addressDetails'],
);

const commitmentsDocument = buildSupporterQueryDocument(
  'GetSupporterCommitments',
  ['commitments'],
);

const givingTotalsDocument = buildSupporterQueryDocument(
  'GetSupporterGivingTotals',
  ['givingTotals'],
);

const pledgesDocument = buildSupporterQueryDocument('GetPledges', ['pledges']);

const contactsDocument = gql`
  ${supporterContactsFragment}
  query GetSupporterContacts($globalId: String!) {
    supporter(globalId: $globalId) {
      contacts {
        ...SupporterContactsFields
      }
    }
  }
`;

const supporterDetailsDocument = buildSupporterQueryDocument(
  'GetSupporterDetails',
  ['details'],
);

const hasCachedSupporterQueryPart = (
  queryClient: QueryClient,
  part: SupporterQueryPart,
  globalId?: string,
) => {
  const queryState = queryClient.getQueryState(
    getSupporterQueryPartKey(part, globalId),
  );
  return queryState !== undefined && queryState.data !== undefined;
};

const getCachedSupporterQueryPartData = <TPart extends SupporterQueryPart>(
  queryClient: QueryClient,
  part: TPart,
  globalId?: string,
) => {
  return queryClient.getQueryData<PrimedSupporterData[TPart]>(
    getSupporterQueryPartKey(part, globalId),
  );
};

const getCachedSupporterBundleData = (
  queryClient: QueryClient,
  parts: readonly SupporterQueryPart[],
  globalId?: string,
) => {
  return parts.reduce<SupporterBundlePartData>((cachedData, part) => {
    if (!hasCachedSupporterQueryPart(queryClient, part, globalId)) {
      return cachedData;
    }

    return {
      ...cachedData,
      [part]: getCachedSupporterQueryPartData(queryClient, part, globalId),
    };
  }, {});
};

const getMissingSupporterQueryParts = (
  queryClient: QueryClient,
  parts: readonly SupporterQueryPart[],
  globalId?: string,
) => {
  return parts.filter(
    (part) => !hasCachedSupporterQueryPart(queryClient, part, globalId),
  );
};

const getDefaultSupporterQueryPartData = <TPart extends SupporterQueryPart>(
  part: TPart,
): PrimedSupporterData[TPart] => {
  switch (part) {
    case 'dataStatus': {
      return defaultSupporterDataStatus as PrimedSupporterData[TPart];
    }
    default: {
      return null as PrimedSupporterData[TPart];
    }
  }
};

/**
 * Extract SupporterDetailsData from a flat supporter object.
 * The fragment spreads fields directly onto the supporter, so they aren't
 * nested under a 'details' key like other data slices.
 */
const extractSupporterDetails = (
  supporter: Record<string, unknown>,
): SupporterDetailsData => ({
  displayName:
    (supporter.displayName as SupporterDetailsData['displayName']) ?? null,
  firstName: (supporter.firstName as SupporterDetailsData['firstName']) ?? null,
  lastName: (supporter.lastName as SupporterDetailsData['lastName']) ?? null,
  preferredName:
    (supporter.preferredName as SupporterDetailsData['preferredName']) ?? null,
  salutation:
    (supporter.salutation as SupporterDetailsData['salutation']) ?? null,
  suffix: (supporter.suffix as SupporterDetailsData['suffix']) ?? null,
  globalId: (supporter.globalId as SupporterDetailsData['globalId']) ?? null,
  organizationName:
    (supporter.organizationName as SupporterDetailsData['organizationName']) ??
    null,
  email: (supporter.email as SupporterDetailsData['email']) ?? null,
  mobilePhone:
    (supporter.mobilePhone as SupporterDetailsData['mobilePhone']) ?? null,
  gender: (supporter.gender as SupporterDetailsData['gender']) ?? null,
  primaryLanguage: (supporter.primaryLanguage as string) ?? null,
  correspondenceDeliveryPreference:
    (supporter.correspondenceDeliveryPreference as string) ?? '',
  isGroup: (supporter.isGroup as SupporterDetailsData['isGroup']) ?? null,
  undeliverableEmail:
    (supporter.undeliverableEmail as SupporterDetailsData['undeliverableEmail']) ??
    null,
  undeliverablePhone:
    (supporter.undeliverablePhone as SupporterDetailsData['undeliverablePhone']) ??
    null,
  type: (supporter.type as SupporterDetailsData['type']) ?? null,
  typeLevel2:
    (supporter.typeLevel2 as SupporterDetailsData['typeLevel2']) ?? null,
  typeLevel3:
    (supporter.typeLevel3 as SupporterDetailsData['typeLevel3']) ?? null,
  status: (supporter.status as string) ?? '',
  recordType:
    (supporter.recordType as SupporterDetailsData['recordType']) ?? null,
  sourceCode:
    (supporter.sourceCode as SupporterDetailsData['sourceCode']) ?? null,
});

const pickSupporterBundleData = (
  supporterData: PrimedSupporterData | GraphQLSupporter,
  parts: readonly SupporterQueryPart[],
) => {
  return parts.reduce<SupporterBundlePartData>((bundleData, part) => {
    // details needs to be extracted from flat fields when the source
    // is a GraphQLSupporter-shaped object (e.g. MOCK_SUPPORTER).
    if (part === 'details' && !(part in supporterData)) {
      return {
        ...bundleData,
        [part]: extractSupporterDetails(
          supporterData as unknown as Record<string, unknown>,
        ),
      };
    }

    return {
      ...bundleData,
      [part]: (supporterData as PrimedSupporterData)[part],
    };
  }, {});
};

const normalizeSupporterBundleData = (
  response: SupporterBundleResponse<SupporterBundlePartData>,
  parts: readonly SupporterQueryPart[],
) => {
  const isResponseError = isGraphQLResponseError(response);

  return parts.reduce<SupporterBundlePartData>((bundleData, part) => {
    if (isResponseError) {
      return {
        ...bundleData,
        [part]: getDefaultSupporterQueryPartData(part),
      };
    }

    // details fields are spread flat on the supporter object
    // (via fragment spread), not nested under a 'details' key.
    if (part === 'details') {
      const supporter = response.supporter;
      return {
        ...bundleData,
        [part]: supporter
          ? extractSupporterDetails(
              supporter as unknown as Record<string, unknown>,
            )
          : getDefaultSupporterQueryPartData(part),
      };
    }

    return {
      ...bundleData,
      [part]:
        response.supporter?.[part] ?? getDefaultSupporterQueryPartData(part),
    };
  }, {});
};

const buildSupporterBundleResult = <
  const TParts extends readonly SupporterQueryPart[],
>(
  parts: TParts,
  supporterData: SupporterBundlePartData,
): SupporterBundleParts<TParts> => {
  return parts.reduce<SupporterBundleParts<TParts>>((bundleData, part) => {
    return {
      ...bundleData,
      [part]: supporterData[part] ?? getDefaultSupporterQueryPartData(part),
    };
  }, {} as SupporterBundleParts<TParts>);
};

const getSupporterBundleData = async (
  globalId: string,
  parts: readonly SupporterQueryPart[],
  operationName = 'GetSupporterBundleData',
): Promise<SupporterBundlePartData> => {
  if (parts.length === 0) {
    return {};
  }

  if (MOCK_SUPPORTER) {
    return pickSupporterBundleData(MOCK_SUPPORTER, parts);
  }

  const response: SupporterBundleResponse<SupporterBundlePartData> =
    await client.request(getSupporterBundleDocument(operationName, parts), {
      globalId,
    });

  return normalizeSupporterBundleData(response, parts);
};

const createSupporterBundleQuery = <
  const TParts extends readonly SupporterQueryPart[],
>(
  queryKey: string,
  operationName: string,
  parts: TParts,
): SupporterQuery<SupporterBundleParts<TParts>> => {
  return {
    queryKey: [queryKey],
    queryFn: async (globalId, queryContext) => {
      const cachedData = queryContext?.queryClient
        ? getCachedSupporterBundleData(
            queryContext.queryClient,
            parts,
            globalId,
          )
        : {};
      const missingParts = queryContext?.queryClient
        ? getMissingSupporterQueryParts(
            queryContext.queryClient,
            parts,
            globalId,
          )
        : [...parts];
      const fetchedData = await getSupporterBundleData(
        globalId,
        missingParts,
        operationName,
      );

      return buildSupporterBundleResult(parts, {
        ...cachedData,
        ...fetchedData,
      });
    },
  };
};

export const primeSupporterData = (
  queryClient: QueryClient,
  supporterData: Partial<PrimedSupporterData>,
  globalId?: string,
) => {
  if ('dataStatus' in supporterData) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('dataStatus', globalId),
      supporterData.dataStatus ?? defaultSupporterDataStatus,
    );
  }

  // TODO: If we want to enable client-side healing, we only want to set the value if we have data
  // if ('commitments' in supporterData && supporterData.commitments) {
  if ('commitments' in supporterData) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('commitments', globalId),
      supporterData.commitments,
    );
  }

  if ('addressDetails' in supporterData) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('addressDetails', globalId),
      supporterData.addressDetails ?? null,
    );
  }

  // TODO: If we want to enable client-side healing, we only want to set the value if we have data
  // if ('paymentMethods' in supporterData && supporterData.paymentMethods) {
  if ('paymentMethods' in supporterData) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('paymentMethods', globalId),
      supporterData.paymentMethods,
    );
  }

  // TODO: If we want to enable client-side healing, we only want to set the value if we have data
  // if ('givingTotals' in supporterData && supporterData.givingTotals) {
  if ('givingTotals' in supporterData) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('givingTotals', globalId),
      supporterData.givingTotals,
    );
  }

  // TODO: If we want to enable client-side healing, we only want to set the value if we have data
  // if ('pledges' in supporterData && supporterData.pledges) {
  if ('pledges' in supporterData) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('pledges', globalId),
      supporterData.pledges,
    );
  }

  if ('details' in supporterData) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('details', globalId),
      supporterData.details ?? null,
    );
  }
};

export const primeSupporterQuery = createSupporterBundleQuery(
  QUERY_KEYS.dataPrimerReadOnly,
  'GetPrimedSupporterData',
  primedSupporterParts,
);

/**
 * Get all commitments.
 *
 * // TODO possibly reduce to what we know we actually need.
 */
export const getCommitments = async (
  globalId: string,
): Promise<SupporterField<'commitments'>> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.commitments ?? [];
  }

  const response: SupporterResponse<'commitments'> = await client.request(
    commitmentsDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.commitments) {
    throw new Error('Empty commitment response.');
  }

  return response.supporter.commitments;
};

/**
 * Query all commitment data
 */
export const commitmentsQuery: SupporterQuery<SupporterField<'commitments'>> = {
  queryKey: [QUERY_KEYS.supporterCommitments],
  queryFn: (globalId) => getCommitments(globalId),
  waitForPrimer: true,
};

export const getGivingTotals = async (
  globalId: string,
): Promise<SupporterField<'givingTotals'> | null> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.givingTotals ?? null;
  }

  const response: SupporterResponse<'givingTotals'> = await client.request(
    givingTotalsDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.givingTotals) {
    throw new Error('Empty giving summary response.');
  }

  return response.supporter?.givingTotals;
};

/**
 * Query giving totals
 */
export const givingTotalsQuery: SupporterQuery<SupporterField<'givingTotals'> | null> =
  {
    queryKey: [QUERY_KEYS.supporterGivingTotals],
    queryFn: async (globalId: string) => getGivingTotals(globalId),
    waitForPrimer: true,
  };

/**
 * Get all payment method information.
 */
export const getPaymentMethods = async (
  globalId: string,
): Promise<SupporterField<'paymentMethods'>> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.paymentMethods ?? [];
  }

  const response = await client.request<SupporterResponse<'paymentMethods'>>(
    paymentMethodsDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.paymentMethods) {
    throw new Error('Empty payment method response.');
  }

  return response.supporter.paymentMethods;
};

/**
 * Query all payment methods.
 * // TODO, clean up TS once we get things going.
 */
export const paymentMethodsQuery: SupporterQuery<
  SupporterField<'paymentMethods'>
> = {
  queryKey: [QUERY_KEYS.supporterPaymentMethods],
  queryFn: (globalId) => getPaymentMethods(globalId),
  waitForPrimer: true,
};

/**
 * Get suppoerter address
 */
export const getSupporterAddress = async (
  globalId: string,
): Promise<SupporterField<'addressDetails'> | null> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.addressDetails ?? null;
  }

  const response: SupporterResponse<'addressDetails'> = await client.request(
    supporterAddressDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  }

  return response.supporter.addressDetails ?? null;
};

export const supporterAddressQuery: SupporterQuery<SupporterField<'addressDetails'> | null> =
  {
    queryKey: [QUERY_KEYS.supporterAddress],
    queryFn: (globalId) => getSupporterAddress(globalId),
    waitForPrimer: true,
  };

export const getPledges = async (
  globalId: string,
): Promise<SupporterField<'pledges'>> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.pledges ?? [];
  }

  const response: SupporterResponse<'pledges'> = await client.request(
    pledgesDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.pledges) {
    throw new Error('Empty pledges response.');
  }

  return response.supporter.pledges;
};

export const pledgesQuery: SupporterQuery<SupporterField<'pledges'>> = {
  queryKey: [QUERY_KEYS.supporterPledges],
  queryFn: async (globalId: string) => getPledges(globalId),
  waitForPrimer: true,
};

export const getSupporterDetails = async (
  globalId: string,
): Promise<SupporterDetailsData | null> => {
  if (MOCK_SUPPORTER) {
    return extractSupporterDetails(
      MOCK_SUPPORTER as unknown as Record<string, unknown>,
    );
  }

  const response: SupporterBundleResponse<SupporterDetailsData> =
    await client.request(supporterDetailsDocument, { globalId });

  if (isGraphQLResponseError(response)) {
    return null;
  }
  return response.supporter
    ? extractSupporterDetails(
        response.supporter as unknown as Record<string, unknown>,
      )
    : null;
};

export const supporterDetailsQuery: SupporterQuery<SupporterDetailsData | null> =
  {
    queryKey: [QUERY_KEYS.supporterDetails],
    queryFn: (globalId) => getSupporterDetails(globalId),
    waitForPrimer: true,
  };

const preferredUsernameDocument = gql`
  query GetPreferredUsername($globalId: String!) {
    supporter(globalId: $globalId) {
      preferredUsername
    }
  }
`;

export const getPreferredUsername = async (
  globalId: string,
): Promise<string | null> => {
  if (MOCK_SUPPORTER) {
    return (
      (MOCK_SUPPORTER as unknown as GraphQLSupporter).preferredUsername ?? null
    );
  }

  try {
    const response: SupporterResponse<'preferredUsername'> =
      await client.request(preferredUsernameDocument, { globalId });

    if (isGraphQLResponseError(response)) {
      return null;
    }

    return response.supporter?.preferredUsername ?? null;
  } catch {
    // Query may fail if the deployed AppSync schema doesn't have preferredUsername yet
    return null;
  }
};

export const preferredUsernameQuery: SupporterQuery<string | null> = {
  queryKey: [QUERY_KEYS.supporterPreferredUsername],
  queryFn: (globalId) => getPreferredUsername(globalId),
};

export const getSupporterContacts = async (
  globalId: string,
): Promise<GraphQLSupporterContact[] | null> => {
  if (MOCK_SUPPORTER) {
    return (MOCK_SUPPORTER as unknown as GraphQLSupporter).contacts ?? null;
  }

  try {
    const response: SupporterResponse<'contacts'> = await client.request(
      contactsDocument,
      { globalId },
    );

    if (isGraphQLResponseError(response)) {
      return null;
    }

    return response.supporter?.contacts ?? null;
  } catch {
    return null;
  }
};

export const supporterContactsQuery: SupporterQuery<
  GraphQLSupporterContact[] | null
> = {
  queryKey: [QUERY_KEYS.supporterContacts],
  queryFn: (globalId) => getSupporterContacts(globalId),
  waitForPrimer: true,
};
